import { useSyncExternalStore } from 'react';
import { API_URL } from './platform';

/**
 * Builds for web game portals (CrazyGames, Poki): `VITE_PORTAL=crazygames|poki`.
 * The portal hosts our static files in its own iframe and owns ads, so a portal
 * build swaps AdSense/Premium for the portal SDK and reports gameplay start/stop.
 * The game itself still talks to our API (VITE_API_URL must point at it).
 */
export type Portal = 'crazygames' | 'poki' | null;
export const PORTAL: Portal = (['crazygames', 'poki'] as const).find((p) => p === import.meta.env.VITE_PORTAL) ?? null;

interface CrazySettings {
  disableChat: boolean;
  muteAudio: boolean;
}
interface CrazySdk {
  init(): Promise<void>;
  game: {
    gameplayStart(): void;
    gameplayStop(): void;
    loadingStart(): void;
    loadingStop(): void;
    happytime(): void;
    settings: CrazySettings;
    addSettingsChangeListener(fn: (s: CrazySettings) => void): void;
    isInstantMultiplayer?: boolean;
    /** Everything the invite carried; null when the game wasn't opened from one. */
    inviteParams?: Record<string, string> | null;
    getInviteParam(key: string): string | null;
    updateRoom(room: { roomId?: string; isJoinable?: boolean; inviteParams?: Record<string, string> }): void;
    leftRoom(): void;
    addJoinRoomListener(fn: (params: Record<string, string>) => void): void;
    inviteLink(params: Record<string, string>): string;
  };
  user: {
    isUserAccountAvailable: boolean;
    getUser(): Promise<{ username: string; profilePictureUrl: string } | null>;
    /** A short-lived JWT naming the signed-in CrazyGames account, checked by our server. */
    getUserToken(): Promise<string>;
    addAuthListener(fn: (user: { username: string } | null) => void): void;
    /** CrazyGames' own sign-in dialog. */
    showAuthPrompt(): Promise<{ username: string } | null>;
    systemInfo?: { locale?: string };
  };
  ad: { requestAd(type: 'midgame', cb: { adStarted?: () => void; adFinished?: () => void; adError?: (e: unknown) => void }): void };
}
interface PokiSdk {
  init(): Promise<void>;
  gameLoadingFinished(): void;
  gameplayStart(): void;
  gameplayStop(): void;
  commercialBreak(onStart?: () => void): Promise<void>;
}
declare global {
  interface Window {
    CrazyGames?: { SDK: CrazySdk };
    PokiSDK?: PokiSdk;
  }
}

const SCRIPTS: Record<Exclude<Portal, null>, string> = {
  crazygames: 'https://sdk.crazygames.com/crazygames-sdk-v3.js',
  poki: 'https://game-cdn.poki.com/scripts/v2/poki-sdk.js',
};

let ready = false;
let playing = false;

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`failed to load ${src}`));
    document.head.appendChild(s);
  });
}

/** Load and init the portal SDK. Never throws: the game must run even if the SDK is blocked. */
export async function initPortal(): Promise<void> {
  if (!PORTAL) return;
  try {
    await Promise.race([
      (async () => {
        await loadScript(SCRIPTS[PORTAL]);
        if (PORTAL === 'crazygames') {
          await window.CrazyGames!.SDK.init();
          window.CrazyGames!.SDK.game.loadingStart();
        } else {
          await window.PokiSDK!.init();
        }
        ready = true;
      })(),
      new Promise((resolve) => setTimeout(resolve, 5000)),
    ]);
  } catch {
    /* ad blocker etc.: play without the SDK */
  }
}

export function loadingFinished(): void {
  if (!ready) return;
  if (PORTAL === 'crazygames') window.CrazyGames?.SDK.game.loadingStop();
  else if (PORTAL === 'poki') window.PokiSDK?.gameLoadingFinished();
}

export function gameplayStart(): void {
  if (!ready || playing) return;
  playing = true;
  if (PORTAL === 'crazygames') window.CrazyGames?.SDK.game.gameplayStart();
  else if (PORTAL === 'poki') window.PokiSDK?.gameplayStart();
}

export function gameplayStop(): void {
  if (!ready || !playing) return;
  playing = false;
  if (PORTAL === 'crazygames') window.CrazyGames?.SDK.game.gameplayStop();
  else if (PORTAL === 'poki') window.PokiSDK?.gameplayStop();
}

/** Celebrate a big moment (CrazyGames only; use sparingly). */
export function happytime(): void {
  if (ready && PORTAL === 'crazygames') window.CrazyGames?.SDK.game.happytime();
}

/** Between games only. Resolves when the ad is over (or there was none); `onStart` should mute audio. */
export function portalBreak(onStart: () => void): Promise<void> {
  if (!ready) return Promise.resolve();
  if (PORTAL === 'poki') return window.PokiSDK!.commercialBreak(onStart).catch(() => {});
  return new Promise((resolve) => {
    window.CrazyGames!.SDK.ad.requestAd('midgame', { adStarted: onStart, adFinished: () => resolve(), adError: () => resolve() });
  });
}

/** Static pages (privacy, terms) live on our own site, not inside the portal's copy of the build. */
export const siteUrl = (path: string) => (PORTAL ? `${API_URL}${path}` : path);

// ---- CrazyGames platform features (all no-ops elsewhere, and without the SDK) ----

const crazy = (): CrazySdk | null => (ready && PORTAL === 'crazygames' ? (window.CrazyGames?.SDK ?? null) : null);

/** The player's language as the portal knows it (e.g. "es-ES"), to use before the browser's. */
export function portalLocale(): string | null {
  try {
    return crazy()?.user.systemInfo?.locale ?? null;
  } catch {
    return null;
  }
}

/** The signed-in portal account's username, so friends recognize each other in game. */
export async function portalUsername(): Promise<string | null> {
  const sdk = crazy();
  if (!sdk?.user.isUserAccountAvailable) return null;
  try {
    return (await sdk.user.getUser())?.username ?? null;
  } catch {
    return null;
  }
}

/**
 * Proof of the player's CrazyGames account for our server (POST /api/auth/crazygames), or null when
 * they aren't signed in to CrazyGames (or it's another portal, or the SDK didn't load).
 */
export async function portalUserToken(): Promise<string | null> {
  const sdk = crazy();
  if (!sdk?.user.isUserAccountAvailable) return null;
  try {
    if (!(await sdk.user.getUser())) return null;
    return await sdk.user.getUserToken();
  } catch {
    return null;
  }
}

/**
 * CrazyGames has player accounts here: the game may offer its sign-in (and must not offer its own,
 * see the portal rules in docs/PORTALS.md).
 */
export function portalAccounts(): boolean {
  return !!crazy()?.user.isUserAccountAvailable;
}

/** Open CrazyGames' sign-in. Signing in there links the player (see onPortalSignIn). */
export function portalSignIn(): void {
  void crazy()?.user.showAuthPrompt().catch(() => {});
}

/** Called with the username when the player signs in to the portal during play. */
export function onPortalSignIn(fn: (username: string) => void): void {
  crazy()?.user.addAuthListener((u) => {
    if (u?.username) fn(u.username);
  });
}

/** The portal can switch chat off (and voice chat with it) and mute the game. */
export function chatAllowed(): boolean {
  return !crazy()?.game.settings.disableChat;
}
export function portalMuted(): boolean {
  return !!crazy()?.game.settings.muteAudio;
}
export function onPortalSettings(fn: () => void): void {
  crazy()?.game.addSettingsChangeListener(() => fn());
}

/** Opened through "play with friends": go straight into multiplayer, no tutorial. */
export function instantMultiplayer(): boolean {
  return !!crazy()?.game.isInstantMultiplayer;
}

/** Opened from a friend's invite: the private room code to join. */
export function invitedRoom(): string | null {
  try {
    // inviteParams first: the page's own ?room= can be left over from an older invite.
    const game = crazy()?.game;
    return game?.inviteParams?.room ?? game?.getInviteParam('room') ?? null;
  } catch {
    return null;
  }
}

/** A friend's invite clicked while already playing. */
export function onPortalJoinRoom(fn: (code: string) => void): void {
  crazy()?.game.addJoinRoomListener((params) => {
    if (params?.room) fn(params.room);
  });
}

/** Tell the portal which private room we're in (for its Join/Invite buttons), or that we left. */
export function portalRoom(room: { code: string; joinable: boolean } | null): void {
  const sdk = crazy();
  if (!sdk) return;
  try {
    if (room) sdk.game.updateRoom({ roomId: room.code, isJoinable: room.joinable, inviteParams: { room: room.code } });
    else sdk.game.leftRoom();
  } catch {
    /* older SDK: invites just aren't offered */
  }
}

/**
 * The SDK keeps the page's query string in the links it makes, so someone who came in through an
 * invite would pass on that old ?room= next to the new code. Make them agree.
 */
export function withRoomParam(link: string, code: string): string {
  try {
    const url = new URL(link);
    if (url.searchParams.has('room')) url.searchParams.set('room', code);
    return url.toString();
  } catch {
    return link;
  }
}

/** A portal invite link to a private room (our own /join links don't exist inside the portal). */
export function portalInviteLink(code: string): string | null {
  try {
    const link = crazy()?.game.inviteLink({ room: code });
    return link ? withRoomParam(link, code) : null;
  } catch {
    return null;
  }
}

const settingsListeners = new Set<() => void>();
let settingsHooked = false;
function subscribeSettings(fn: () => void): () => void {
  settingsListeners.add(fn);
  if (!settingsHooked) {
    settingsHooked = true;
    onPortalSettings(() => settingsListeners.forEach((l) => l()));
  }
  return () => settingsListeners.delete(fn);
}

/** Chat (and voice) allowed by the portal, updating live if its settings change. Always true off-portal. */
export function useChatAllowed(): boolean {
  return useSyncExternalStore(subscribeSettings, chatAllowed, () => true);
}
